import { useMemo } from 'react'
import { getBaseRole } from '@/helpers/customRoles'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { IEditMode } from '../Table'
import {
  createRoleSettings,
  DEFAULT_ROLE_KEY,
  EMPTY_ROLE_DEFAULT_COLUMNS,
  parseRoleBasedTableSettings,
  RoleDefaultColumns,
} from './utils'

interface UseRoleBasedTableSettingsParams {
  settingKey: string
  columnsMap: { value: string; label: string }[]
  defaultRoleKey?: string
  roleDefaultColumns?: RoleDefaultColumns
}

export const useRoleBasedTableSettings = ({
  settingKey,
  columnsMap,
  defaultRoleKey = DEFAULT_ROLE_KEY,
  roleDefaultColumns = EMPTY_ROLE_DEFAULT_COLUMNS,
}: UseRoleBasedTableSettingsParams) => {
  const userRole = useSelector(selectProfile)?.role
  const defaultColumns = useMemo(
    () => columnsMap.map((item) => item.value),
    [columnsMap],
  )

  const { storedSettings } = useSelector((store: any) => {
    const rawSettings = store?.settings?.custom?.[settingKey]

    return {
      storedSettings: parseRoleBasedTableSettings({
        value: rawSettings,
        settingKey,
        defaultColumns,
        defaultRoleKey,
        roleDefaultColumns,
      }),
    }
  })

  const [columnOrder, hiddenColumns] = useMemo(() => {
    const allColumns = [...defaultColumns]
    const roleDefaultSettings = userRole
      ? roleDefaultColumns[getBaseRole(userRole)]
      : undefined

    const roleSettings =
      storedSettings?.forRoles?.find((it) => it.role === userRole) ??
      (userRole && roleDefaultSettings
        ? createRoleSettings(userRole, roleDefaultSettings)
        : undefined) ??
      storedSettings?.forRoles?.find((it) => it.role === defaultRoleKey) ??
      createRoleSettings(defaultRoleKey, defaultColumns)

    const orderedKnown = roleSettings.columns.filter((it) =>
      allColumns.includes(it),
    )

    const missingColumns = allColumns.filter((it) => !orderedKnown.includes(it))
    const fullColumnOrder = [...orderedKnown, ...missingColumns]
    const hiddenColumns = allColumns.filter(
      (it) => !roleSettings.columns.includes(it),
    )

    return [fullColumnOrder, hiddenColumns]
  }, [
    defaultColumns,
    defaultRoleKey,
    roleDefaultColumns,
    storedSettings,
    userRole,
  ])

  const editModeConfig: IEditMode = {
    columnOrder,
    hiddenColumns,
    active: false,
    mode: [],
    onChange: () => {
      throw new Error('Function not implemented.')
    },
  }

  return {
    settings: editModeConfig,
  }
}
